/**
 * The count-card fold — one card per working-set group.
 *
 * Q4 decision (link-don't-merge project): the fold is a DISPLAY fold. A
 * related group's members remain separate inventory items with separate
 * count_items rows; at count time the session shows ONE card — the favored
 * member's — and the typed quantity lands on that member's own row exactly as
 * it always did (count-typed-value-is-sacrosanct). Sibling members' rows are
 * hidden, never rewritten: their stored counts and PARs keep contributing to
 * the group's math through two stamps carried on the favored member's first
 * row:
 *
 *   group_baseline_count — the sum of hidden rows' typed counts (legacy
 *                          sessions where several members were counted). Need
 *                          math adds it to on-hand, so 3 lime items with 12
 *                          counted across rows order 0, not 8.
 *   group_par_floor      — the max hidden-row NORMALIZED PAR (resolveRowPar:
 *                          stored par_normalized_quantity wins; a legacy
 *                          value in the row's own count unit converts; an
 *                          ambiguous "2 cases" contributes nothing and marks
 *                          group_par_unresolved instead). Need math takes
 *                          max(visible PAR, floor) — the same normalize-to-max
 *                          rule sibling location rows have always used.
 *
 * One CANONICAL card per group, always (relationship domain model, AJ
 * 2026-08-19), on a STABLE stock basis: the BASIS member (the sticky group
 * canonical when eligible, else the lowest eligible id) owns the group's
 * NORMALIZED unit — the unit every stored count and PAR is denominated in,
 * the row a typed number lands on, and the id the allocation line is keyed
 * to. The FAVORED member fronts the card only when it shares that unit.
 * Odd-unit members' rows hide — the group is ONE inventory need, and their
 * purchase options still shop through the pooled group — with two sacrosanct
 * exceptions that stay visible: a row carrying a TYPED COUNT (never summed
 * across dimensions, never vanished), and an uncounted row carrying the
 * group's ONLY PAR (hiding it would silently drop the group from ordering).
 * A group with fewer than two present members has nothing to fold.
 *
 * WHAT THE BASIS NO LONGER DECIDES (superseded 2026-08-21). The basis member
 * used to own the group's human-facing count/PAR/NEED UNIT as well, so a
 * store buying Diet Coke in 5 gal BIBs counted it in gallons whenever the
 * sticky canonical happened to be a gallon-counted sibling. Identity and
 * expression are now separate concerns: the basis still owns identity and
 * the normalized quantities, while the package those quantities are READ in
 * is the group's OPERATIONAL ANCHOR (`utils/groupCountAnchor`), which follows
 * durable operational evidence and may belong to any present member,
 * whatever its own unit. The physical target is protected by staying
 * normalized and converting only for display — not by freezing how it reads.
 * `presentItemIds` below carries every present member so the anchor can pool
 * across dimensions; `memberItemIds` remains the same-unit partition whose
 * counts and PARs may legitimately be summed.
 *
 * Pure so the fold is testable without a component; its one dependency is the
 * count-unit model, because the PAR floor must come from the same normalized
 * PAR resolution every other need implementation uses.
 */

import { resolveRowPar, PAR_STATUS } from './countUnitModel.js';

const toFinite = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const rowInvId = (row) => row?.inventory_item_id ?? row?.item_snapshot?.id ?? null;

// The same "was this row actually counted" rule rowOnHand and
// buildNeedsFromCountItems use: is_counted, or a real current_count entry.
const rowCountedQuantity = (row) => {
  const counted = !!row?.is_counted
    || (row?.current_count !== '' && row?.current_count !== null && row?.current_count !== undefined);
  if (!counted) return null;
  return toFinite(row?.actual_quantity ?? row?.current_count) ?? 0;
};

// The row's own count unit, unfolded — what resolveRowPar compares par_unit
// against, exactly as countPresentation/orderRecalc pass the session unit.
// Deliberately NO par_unit fallback: comparing a PAR's unit against itself
// makes every gate trivially pass, which is how "2 cases" on a unit-less
// item silently became the number 2. A missing item unit stays missing and
// the stated PAR reports unresolved, matching the server engine.
const rowCountUnit = (row) => row?.item_snapshot?.unit ?? row?.count_unit ?? '';

/**
 * The row's PAR through the ONE normalized resolution (stored
 * par_normalized_quantity wins; legacy par_level only when its unit already IS
 * the row's count unit). A legacy "2 cases" with no identifiable package
 * resolves to nothing here — it must never join a floor as the number 2.
 */
const rowResolvedPar = (row) => {
  const resolution = resolveRowPar(row, rowCountUnit(row));
  return resolution.status === PAR_STATUS.VALID ? resolution.normalized : null;
};

/** Does the row STATE a PAR at all (resolvable or not)? Drives visibility. */
const rowStatesPar = (row) => {
  const resolution = resolveRowPar(row, rowCountUnit(row));
  if (resolution.status === PAR_STATUS.VALID && resolution.normalized !== null) return true;
  return resolution.display?.value != null;
};

/** A stated PAR the normalized resolution could not turn into a number. */
const rowParUnresolved = (row) => {
  const resolution = resolveRowPar(row, rowCountUnit(row));
  return resolution.status !== PAR_STATUS.VALID && resolution.display?.value != null;
};

// The row's count unit, normalized for the compatibility gate. '' and null
// read as 'each' — the default unit everywhere else in counting.
export const foldUnitKey = (row) => {
  const raw = row?.item_snapshot?.unit ?? row?.count_unit ?? row?.par_unit ?? '';
  const key = String(raw ?? '').toLowerCase().trim();
  if (key === '' || key === 'ea') return 'each';
  return key;
};

/**
 * A groups view restricted to the items present in a session: membership is
 * intersected with `presentItemIds`, and groups left with fewer than two
 * present members drop out. Used both to bound the favored-member recency
 * fetch and to drive the fold itself.
 *
 * @param {{membersByGroupId: Map}} groups  buildWorkingSetGroups output
 * @param {Iterable} presentItemIds
 * @returns {{ groupIdByItemId: Map, membersByGroupId: Map }}
 */
export function restrictGroupsToItems(groups, presentItemIds) {
  const present = new Set([...(presentItemIds || [])].map(String));
  const membersByGroupId = new Map();
  const groupIdByItemId = new Map();
  (groups?.membersByGroupId ?? new Map()).forEach((members, groupId) => {
    const kept = (members || []).map(String).filter((id) => present.has(id));
    if (kept.length < 2) return;
    membersByGroupId.set(String(groupId), kept);
    kept.forEach((id) => groupIdByItemId.set(id, String(groupId)));
  });
  return { groupIdByItemId, membersByGroupId };
}

/**
 * Partition a session's rows into the visible list and the hidden sibling
 * rows, stamping the favored member's first row with the group's math facts.
 *
 * @param {object} args
 * @param {Array}  args.rows              raw count_items rows, session order
 * @param {object} args.groups            RESTRICTED groups (restrictGroupsToItems)
 * @param {Map}    [args.favoredByGroupId] group id → favored member item id
 *                                        (resolveFavoredMembers). Which ROW
 *                                        fronts the card: the favored member
 *                                        when it shares the group's normalized
 *                                        unit, else the basis member. It never
 *                                        moves the basis. (Which PACKAGE the
 *                                        card counts in is a separate
 *                                        question — see groupCountAnchor.)
 * @param {Map}    [args.stickyCanonicalByGroupId] group id → the group's STICKY
 *                                        canonical member (equivalent_group_
 *                                        comparisons.canonical_item_id). The
 *                                        identity anchor: its unit is the unit
 *                                        the group's quantities are STORED in.
 *                                        Absent entries fall back to the
 *                                        lowest eligible member id — stable
 *                                        either way, so a recency flip can
 *                                        never re-denominate a stored PAR or
 *                                        re-key a need line.
 * @param {function} [args.unitOf]        row → unit key; defaults foldUnitKey
 * @returns {{ visibleRows: Array, hiddenRows: Array,
 *             foldsByGroupId: Map<string, {favoredItemId, basisItemId,
 *             memberItemIds, presentItemIds, hiddenRowCount, baselineCount,
 *             parFloor, parUnresolved}> }}
 *   visibleRows preserves input order; stamped rows are copies (input rows
 *   are never mutated).
 */
export function planCountGroupFold({ rows, groups, favoredByGroupId, stickyCanonicalByGroupId, unitOf = foldUnitKey } = {}) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const membersByGroupId = groups?.membersByGroupId ?? new Map();
  if (!membersByGroupId.size) {
    return { visibleRows: list, hiddenRows: [], foldsByGroupId: new Map() };
  }

  const rowsByInvId = new Map();
  list.forEach((row) => {
    const invId = rowInvId(row);
    if (invId === null || invId === undefined) return;
    const key = String(invId);
    if (!rowsByInvId.has(key)) rowsByInvId.set(key, []);
    rowsByInvId.get(key).push(row);
  });

  const hiddenRowSet = new Set();
  const stampByRow = new Map(); // first favored row → stamp fields
  const foldsByGroupId = new Map();

  membersByGroupId.forEach((memberIds, groupId) => {
    const present = (memberIds || []).map(String).filter((id) => rowsByInvId.has(id));
    if (present.length < 2) return;

    // Idempotence: a group whose rows already carry a fold stamp was folded
    // upstream (the count screen), and its hidden siblings are NOT in these
    // rows any more — re-folding would rebuild the stamp from what remains,
    // erasing the carried baseline/floor (or hiding the stamped row outright
    // when an odd-unit exception row keeps the group ≥2 members here). The
    // settled fold stands; its rows pass through untouched.
    if (present.some((id) => rowsByInvId.get(id).some((row) => row?.group_fold))) {
      return;
    }

    // One CANONICAL card per group, on a STABLE IDENTITY. Members whose own
    // rows agree on a unit are fold-eligible. The BASIS member anchors the
    // group's NORMALIZED unit: the sticky group canonical when it is eligible
    // (equivalent_group_comparisons.canonical_item_id — re-elected only on
    // membership change, never on recency or price), else the lowest eligible
    // id. The FAVORED member decides which ROW fronts the card: it does so
    // when it shares the basis unit, and an odd-unit favored member can never
    // drag the group's STORED PAR into its own unit — a stored quantity must
    // stay in the dimension it was written in. (What that quantity is READ in
    // is the operational anchor's business, not this one's: the anchor may be
    // an odd-unit member's package, converted for display through the shared
    // package resolver. See the module header.) Same-unit
    // siblings fold into the card (typed counts ride as the baseline, their
    // normalized PARs as the floor); odd-unit members never sum — a quantity
    // must not cross dimensions ("2 each + 3 lb") — so their rows either hide
    // or stay, per the rules below. The member's PURCHASE OPTIONS still shop
    // through the pooled group regardless: hiding a count row never hides a
    // way to buy.
    const unitByMember = new Map();
    present.forEach((id) => {
      const keys = new Set(rowsByInvId.get(id).map((row) => unitOf(row)));
      if (keys.size === 1) unitByMember.set(id, [...keys][0]);
    });
    const eligible = present.filter((id) => unitByMember.has(id));
    if (!eligible.length) return;

    const stickyCandidate = stickyCanonicalByGroupId?.get?.(String(groupId));
    const basisItemId = stickyCandidate && eligible.includes(String(stickyCandidate))
      ? String(stickyCandidate)
      : eligible[0]; // member order = ascending ids
    const foldUnit = unitByMember.get(basisItemId);
    const favoredCandidate = favoredByGroupId?.get?.(String(groupId));
    const canonicalItemId = favoredCandidate
      && eligible.includes(String(favoredCandidate))
      && unitByMember.get(String(favoredCandidate)) === foldUnit
      ? String(favoredCandidate)
      : basisItemId;
    const partitionMembers = eligible.filter((id) => unitByMember.get(id) === foldUnit);

    let baselineCount = 0;
    let baselineCounted = false;
    let parFloor = null;
    let parFloorDefaulted = false;
    let parUnresolved = false;
    let hiddenRowCount = 0;
    let hiddenOddUnitRows = 0;
    // Every member's own stated stocking level, kept apart from the floor.
    // Two members of an Equivalent group are two PRODUCTS, so two different
    // PARs are two different stocking policies — not two spellings of one.
    // The floor still resolves the group by max (an order must stay
    // buyable), but a disagreement is now recorded instead of vanishing into
    // the larger number. Same-item location rows are a different question and
    // are still normalized to the max upstream without comment: there the
    // divergence really is a data artifact.
    const parByMember = new Map();

    // Same-unit siblings: fold. The floor is the max NORMALIZED sibling PAR —
    // a stated PAR the resolution refuses ("2 cases", no package) contributes
    // nothing and instead marks the group unresolved, because folding the raw
    // 2 into an each-basis floor is how a package count becomes a quantity.
    partitionMembers.forEach((id) => {
      if (id === canonicalItemId) return;
      rowsByInvId.get(id).forEach((row) => {
        hiddenRowSet.add(row);
        hiddenRowCount += 1;
        const counted = rowCountedQuantity(row);
        if (counted !== null) {
          baselineCount += counted;
          baselineCounted = true;
        }
        const par = rowResolvedPar(row);
        if (par !== null && (parFloor === null || par > parFloor)) {
          parFloor = par;
          parFloorDefaulted = row.par_defaulted === true;
        } else if (par !== null && par === parFloor && row.par_defaulted === true) {
          // If equal winning PARs include an untouched creation default, the
          // folded card still represents an item that needs human review.
          parFloorDefaulted = true;
        }
        if (par !== null && (!parByMember.has(id) || par > parByMember.get(id))) {
          parByMember.set(id, par);
        }
        if (rowParUnresolved(row)) parUnresolved = true;
      });
    });

    // The canonical member's own PAR joins the comparison — it is the policy
    // the order event will actually run on, so a sibling quietly overriding it
    // is exactly what has to be visible.
    rowsByInvId.get(canonicalItemId).forEach((row) => {
      const par = rowResolvedPar(row);
      if (par !== null && (!parByMember.has(canonicalItemId) || par > parByMember.get(canonicalItemId))) {
        parByMember.set(canonicalItemId, par);
      }
    });

    // A disagreement, in the group's own count unit — never across dimensions,
    // because odd-unit members never contributed a PAR in the first place.
    // Tolerance absorbs stored rounding (a normalized PAR is 6dp), not a real
    // difference in policy.
    const statedPars = [...parByMember.entries()]
      .map(([itemId, normalized]) => ({ itemId, normalized }))
      .sort((a, b) => b.normalized - a.normalized);
    const parConflict = statedPars.length >= 2
      && (statedPars[0].normalized - statedPars[statedPars.length - 1].normalized) > 1e-6
      ? {
        unit: foldUnit,
        // Highest first, so the reader sees the spread immediately.
        stated: statedPars,
        // What the order runs on until somebody decides. Named, so the card
        // can say "using 12" rather than leaving the buyer to infer it.
        using: statedPars[0].normalized,
        usingItemId: statedPars[0].itemId,
      }
      : null;

    // Does the canonical card have a PAR at all (its own rows or the folded
    // floor)? Decides whether an odd-unit PAR may be hidden below. A STATED
    // but unresolvable PAR counts — the row still carries the group's only
    // stocking intent, and hiding it would silently drop that intent.
    const canonicalHasPar = parFloor !== null
      || rowsByInvId.get(canonicalItemId).some(rowStatesPar)
      || partitionMembers.some((id) => rowsByInvId.get(id).some(rowStatesPar));

    // Odd-unit members: the group is ONE inventory need, counted on the
    // canonical card in the canonical's unit — so their rows hide, with two
    // sacrosanct exceptions. A COUNTED row always stays visible (a typed
    // quantity is the record of the real world and must never be summed
    // into another dimension or silently vanish). An UNCOUNTED row carrying
    // the group's ONLY PAR stays visible too — hiding it would leave the
    // whole group with no PAR, no need, and no order in every need
    // implementation.
    present.forEach((id) => {
      if (partitionMembers.includes(id)) return;
      rowsByInvId.get(id).forEach((row) => {
        if (rowCountedQuantity(row) !== null) return; // typed count: visible
        if (!canonicalHasPar && rowStatesPar(row)) return; // only PAR: visible
        hiddenRowSet.add(row);
        hiddenRowCount += 1;
        hiddenOddUnitRows += 1;
        // NO baseline, NO par floor: nothing crosses the dimension.
      });
    });

    const favoredRows = rowsByInvId.get(canonicalItemId);
    stampByRow.set(favoredRows[0], {
      group_fold: {
        groupId: String(groupId),
        favoredItemId: canonicalItemId,
        basisItemId,
        memberItemIds: partitionMembers,
        // Every member present in this session, odd-unit ones included. The
        // operational anchor pools purchase options across all of them —
        // the 5 gal BIB that a group is really counted in commonly sits on
        // a member whose own unit differs from the basis.
        presentItemIds: present,
        hiddenRowCount,
      },
      // Absent (null) when no hidden row was counted — "no on-hand
      // evidence" and "counted as zero" are different states, and the need
      // math's not-counted gate must keep telling them apart.
      group_baseline_count: baselineCounted ? baselineCount : null,
      group_par_floor: parFloor,
      group_par_defaulted: parFloor !== null ? parFloorDefaulted : undefined,
      // A hidden sibling STATED a PAR the normalized resolution refused.
      // Consumers surface it as needing package resolution rather than
      // pretending the group's PAR is whatever did resolve.
      group_par_unresolved: parUnresolved || undefined,
      // Members of this group state DIFFERENT stocking levels. Carried so a
      // surface can ask which policy the group should keep, instead of the
      // larger number silently winning forever.
      group_par_conflict: parConflict || undefined,
    });
    foldsByGroupId.set(String(groupId), {
      favoredItemId: canonicalItemId,
      basisItemId,
      memberItemIds: partitionMembers,
      presentItemIds: present,
      hiddenRowCount,
      hiddenOddUnitRows,
      baselineCount: baselineCounted ? baselineCount : null,
      parFloor,
      parFloorDefaulted,
      parUnresolved,
      parConflict,
    });
  });

  if (!hiddenRowSet.size && !stampByRow.size) {
    return { visibleRows: list, hiddenRows: [], foldsByGroupId };
  }

  const visibleRows = [];
  const hiddenRows = [];
  list.forEach((row) => {
    if (hiddenRowSet.has(row)) {
      hiddenRows.push(row);
      return;
    }
    const stamp = stampByRow.get(row);
    visibleRows.push(stamp ? { ...row, ...stamp } : row);
  });

  return { visibleRows, hiddenRows, foldsByGroupId };
}
